import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { DownloadSimple, Trash } from "@phosphor-icons/react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { API_BASE, api, errMsg } from "@/lib/api";
import { inputCls } from "@/lib/ui";
import { fmtDate, money } from "@/components/admin/format";

export const SessionsPanel = ({ sessions: initial }) => {
  const [sessions, setSessions] = useState(initial);
  useEffect(() => setSessions(initial), [initial]);
  const [owner, setOwner] = useState("");
  const [q, setQ] = useState("");
  const [confirm, setConfirm] = useState(null);
  const remove = async (s) => {
    try {
      await api.delete(`/sessions/${s.id}`);
      setSessions((rows) => rows.filter((x) => x.id !== s.id));
      toast.success("Meeting deleted");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const owners = useMemo(() => {
    const m = new Map();
    sessions.forEach((s) => m.set(s.owner || "", s.owner_name || "Unassigned"));
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [sessions]);
  const shown = sessions.filter((s) => (!owner || (s.owner || "") === owner) && (!q || `${s.title} ${s.id}`.toLowerCase().includes(q.toLowerCase())));

  return (
    <div className="border border-[var(--o-border)]" data-testid="sessions-panel">
      <div className="px-5 py-4 border-b border-[var(--o-border)] flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-display text-base md:text-lg font-bold">Meetings</h2>
          <p className="text-xs text-[var(--o-muted2)] mt-1">Everyone's saved meetings. Private meetings keep no transcript. Stored on this server's disk.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <select className={`${inputCls} !w-auto text-xs`} value={owner} onChange={(e) => setOwner(e.target.value)} aria-label="Filter by person" data-testid="sessions-owner-filter">
            <option value="">Everyone ({sessions.length})</option>
            {owners.map(([id, name]) => <option key={id} value={id}>{name} ({sessions.filter((s) => (s.owner || "") === id).length})</option>)}
          </select>
          <input className={`${inputCls} !w-44 text-xs`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title" aria-label="Search meetings" data-testid="sessions-search" />
        </div>
      </div>
      {shown.length === 0 && <p className="px-5 py-6 text-sm text-[var(--o-muted3)]" data-testid="sessions-empty">{sessions.length ? "No meetings match." : "No meetings yet."}</p>}
      {shown.map((s) => (
        <div key={s.id} className="grid grid-cols-[1fr_auto] md:grid-cols-[1fr_150px_70px_150px_auto] gap-3 items-center px-5 py-3 border-b border-[var(--o-border-soft)] last:border-b-0 text-sm" data-testid={`session-row-${s.id}`}>
          <div className="min-w-0">
            <div className="truncate font-semibold">{s.title}</div>
            <div className="font-mono text-[11px] text-[var(--o-muted3)] truncate">{fmtDate(s.created_at)}{s.recording ? ` · ${s.recording}` : ""}{s.cost != null ? ` · ${money(s.cost)}` : ""}</div>
          </div>
          <span className="text-xs text-[var(--o-muted)] truncate hidden md:block" data-testid={`session-owner-${s.id}`}>{s.owner_name}</span>
          <span className={`font-mono text-[10px] uppercase px-1.5 py-0.5 border w-fit hidden md:block ${s.mode === "replay" ? "border-[#F59E0B]/40 text-[#F59E0B]" : "border-[var(--o-border-strong)] text-[var(--o-muted)]"}`}>{s.mode}</span>
          <span className="font-mono text-[11px] text-[var(--o-muted2)] hidden md:block truncate">{s.answers} ans · {s.flags} checks</span>
          <div className="flex gap-2 items-center">
            {s.live_in_memory && <Link to={`/session/${s.id}`} className="font-mono text-xs text-[#10B981] hover:underline" data-testid={`session-open-${s.id}`}>open</Link>}
            <Link to={`/score/${s.id}`} className="font-mono text-xs border border-[var(--o-border)] px-2 py-1 hover:bg-[var(--o-surface)] transition-colors" data-testid={`session-score-${s.id}`}>
              score {s.scoring ? `${Math.min(s.scoring.progress.a, s.scoring.progress.b)}/${s.scoring.items}` : ""}{s.scoring?.verdict && s.scoring.verdict !== "incomplete" ? ` · ${s.scoring.verdict}` : ""}
            </Link>
            <a href={`${API_BASE}/sessions/${s.id}/download`} className="text-[var(--o-muted)] hover:text-[var(--o-fg)] p-1" title="Download JSON" data-testid={`session-download-${s.id}`}><DownloadSimple size={15} /></a>
            <button type="button" onClick={() => setConfirm(s)} disabled={s.live_in_memory} className="text-[var(--o-muted)] hover:text-[#EF4444] disabled:opacity-40 p-1" title={s.live_in_memory ? "End the meeting first" : "Delete this meeting's log and any kept audio"} data-testid={`session-delete-${s.id}`}><Trash size={15} /></button>
          </div>
        </div>
      ))}
      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent className="bg-[var(--o-bg2)] border-[var(--o-border)] rounded-none" data-testid="session-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl">Delete “{confirm?.title}”?</AlertDialogTitle>
            <AlertDialogDescription className="text-[var(--o-muted)]">
              {confirm?.owner_name ? `${confirm.owner_name}'s meeting. ` : ""}The log and any audio still kept for it are removed from this server for good. This is written to the activity log.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none border-[var(--o-border)] bg-transparent">Keep it</AlertDialogCancel>
            <AlertDialogAction className="rounded-none bg-[#EF4444] text-white hover:opacity-90" onClick={() => remove(confirm)} data-testid="session-confirm-yes">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
